import ReportIssueBar from '@/components/ReportIssueBar';
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Upload, FileText, AlertCircle, Search, XCircle, FileSpreadsheet, X, Download } from "lucide-react";
import axios from "axios";
import toast from 'react-hot-toast';
import MatrixBackground from "@/MatrixBackground";
import AnimatedToolTitle from '@/components/AnimatedToolTitle';
import AnimatedActionButton from '@/components/AnimatedActionButton';
import LedProgressLine from "@/components/LedProgressLine";
import { Document, Paragraph, TextRun, HeadingLevel, AlignmentType, Packer } from "docx";
import { saveAs } from "file-saver";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

const getToken = () => {
  const token = localStorage.getItem('token');
  if (token) return token;
  const trialUser = localStorage.getItem('trial_user');
  if (trialUser) return 'trial_user_token';
  return null;
};

const isResultExpired = (createdAt) => {
  const created = new Date(createdAt);
  const now = new Date();
  const hoursDiff = (now - created) / (1000 * 60 * 60);
  return hoursDiff > 6; // Results expire after 6 hours
};

const DueDiligence = ({ onBack }) => {
    // Set page title
  useEffect(() => {
    document.title = "Due Diligence - Lawra AI";
    return () => {
      document.title = "Lawra AI - AI Legal Assistant for Lawyers & Law Firms | Save 70-80% Time";
    };
  }, []);

  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);

  useEffect(() => {
    // Check if current results are expired
    if (analysisResults && analysisResults.created_at && isResultExpired(analysisResults.created_at)) {
      setAnalysisResults(null);
      toast.info('Previous results expired. Please generate a new analysis.');
    }
  }, []);

  const handleDownloadReport = async () => {
    if (!analysisResults) return;

    try {
      const stripHtml = (html) => {
        if (!html) return '';
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
      };

      const children = [];

      // Title
      children.push(
        new Paragraph({
          text: "Due Diligence Report",
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 }
        })
      );

      // Executive Summary
      if (analysisResults.executive_summary) {
        children.push(
          new Paragraph({
            text: "Executive Summary",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 300, after: 200 }
          }),
          new Paragraph({
            text: stripHtml(analysisResults.executive_summary),
            spacing: { after: 300 }
          })
        );
      }

      // Key Findings
      if (analysisResults.key_findings && analysisResults.key_findings.length > 0) {
        children.push(
          new Paragraph({
            text: "Key Findings",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 300, after: 200 }
          })
        );
        analysisResults.key_findings.forEach((finding, index) => {
          children.push(
            new Paragraph({
              text: `${index + 1}. ${finding}`,
              spacing: { after: 100 },
              indent: { left: 720 }
            })
          );
        });
      }

      // Red Flags
      if (analysisResults.red_flags && analysisResults.red_flags.length > 0) {
        children.push(
          new Paragraph({
            text: "Red Flags",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 300, after: 200 }
          })
        );
        analysisResults.red_flags.forEach((flag, index) => {
          children.push(
            new Paragraph({
              text: `${index + 1}. ${flag.title} (${flag.severity} Risk)`,
              heading: HeadingLevel.HEADING_3,
              spacing: { before: 200, after: 100 }
            }),
            new Paragraph({
              text: `Category: ${flag.category}`,
              spacing: { after: 50 },
              indent: { left: 720 }
            }),
            new Paragraph({
              text: `Description: ${flag.description}`,
              spacing: { after: 50 },
              indent: { left: 720 }
            }),
            new Paragraph({
              text: `Recommendation: ${flag.recommendation}`,
              spacing: { after: 200 },
              indent: { left: 720 }
            })
          );
        });
      }

      // Recommendations
      if (analysisResults.recommendations && analysisResults.recommendations.length > 0) {
        children.push(
          new Paragraph({
            text: "Recommendations",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 300, after: 200 }
          })
        );
        analysisResults.recommendations.forEach((rec, index) => {
          children.push(
            new Paragraph({
              text: `${index + 1}. ${rec}`,
              spacing: { after: 100 },
              indent: { left: 720 }
            })
          );
        });
      }

      // Footer
      children.push(
        new Paragraph({
          text: `Generated by Lawra on ${new Date().toLocaleDateString()}`,
          alignment: AlignmentType.CENTER,
          spacing: { before: 400 }
        })
      );

      const doc = new Document({
        sections: [{ properties: {}, children: children }]
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, `Due_Diligence_${new Date().toISOString().split('T')[0]}.docx`);
      toast.success('Report downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Error downloading report. Please try again.');
    }
  };

  const handleFilesChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    if (selectedFiles.length > 10) {
      toast.error("Error");
      return;
    }
    
    // Check file sizes
    for (const file of selectedFiles) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`File ${file.name} exceeds 10MB limit`);
        return;
      }
    }
    
    setFiles(selectedFiles);
    setAnalysisResults(null);
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleAnalyze = async () => {
    console.log('handleAnalyze called, files:', files);
    
    if (files.length === 0) {
      console.log('No files selected');
      toast.error("Error");
      return;
    }

    console.log('Starting analysis, API URL:', `${API}/due-diligence/analyze`);
    setAnalyzing(true);
    const formData = new FormData();
    
    files.forEach((file, index) => {
      formData.append(`files`, file);
    });

    try {
      console.log('Sending request...');
      const response = await axios.post(
        `${API}/due-diligence/analyze`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      console.log('Analysis response received:', response.data);
      setAnalysisResults(response.data);
      toast.success("Success");
    } catch (error) {
      console.error('Analysis error:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      toast.error("Error");
    } finally {
      console.log('Analysis complete, setting analyzing to false');
      setAnalyzing(false);
    }
  };

  const handleReset = () => {
    setFiles([]);
    setAnalysisResults(null);
    if (document.getElementById('files-input')) {
      document.getElementById('files-input').value = '';
    }
  };

  // Utility functions removed - no longer needed for due diligence component

  return (
    <div className="tool-page">
      <MatrixBackground />
      <div className="tool-header">
        <Button
          variant="ghost"
          onClick={() => onBack ? onBack() : navigate('/dashboard')}
          className="back-button"
        >
          <ArrowLeft size={20} />
          Back to Dashboard
        </Button>
        <div className="tool-title-section">
          <Search size={40} className="tool-icon gold-icon" />
          <div>
            <AnimatedToolTitle title="Due Diligence" />
            <p className="tool-subtitle">Comprehensive analysis of multiple documents for investment and business decisions</p>
          </div>
        </div>
      </div>

      <div className="tool-content">
        {!analysisResults ? (
          <>
            {/* Upload and Analysis Card */}
            <Card className="generation-form-card">
              <CardHeader>
                <CardTitle>Document Analysis & Due Diligence</CardTitle>
                <CardDescription>
                  Upload multiple documents (contracts, financials, reports) for comprehensive due diligence analysis
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="form-group">
                  <label className="upload-label">Documents (up to 10 files) *</label>
                  <div className="file-upload-area">
                    <input
                      type="file"
                      id="files-input"
                      accept=".pdf,.docx,.xlsx,.xls,.txt"
                      multiple
                      onChange={handleFilesChange}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="files-input" className="file-upload-label">
                      <div className="upload-icon-tool">
                        {files.length > 0 ? <FileSpreadsheet size={48} /> : <Upload size={48} />}
                      </div>
                      <p className="upload-text-tool">
                        {files.length > 0 ? `${files.length} file(s) selected` : 'Click to upload documents'}
                      </p>
                      <p className="upload-subtext-tool">PDF, DOCX, XLSX, XLS, TXT • Max 10MB per file</p>
                    </label>
                  </div>
                  
                  {/* Selected Files List */}
                  {files.length > 0 && (
                    <div className="selected-files-list">
                      {files.map((file, index) => (
                        <div key={index} className="selected-file-item">
                          <FileText size={16} />
                          <span className="file-name">{file.name}</span>
                          <span className="file-size">({(file.size / 1024 / 1024).toFixed(1)}MB)</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFile(index)}
                            className="remove-file-btn"
                          >
                            <XCircle size={16} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-actions">
                  <AnimatedActionButton
                onClick={handleAnalyze}
                disabled={analyzing || files.length === 0}
                loading={analyzing}
                size="lg"
              >
                Analyze
              </AnimatedActionButton>
                  <LedProgressLine isActive={analyzing} />
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          /* Results View */
          <>
            <div className="results-header">
              <h2>Due Diligence Report</h2>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <Button onClick={handleDownloadReport} variant="outline">
                  <Download size={18} style={{ marginRight: '8px' }} />
                  Download Report
                </Button>
                <Button onClick={handleReset} variant="outline">
                  New Analysis
                </Button>
              </div>
            </div>

            {/* Executive Summary & Risk Rating */}
            <Card className="result-card">
              <CardHeader>
                <div className="risk-summary-header">
                  <Search size={24} className="gold-icon" />
                  <div>
                    <CardTitle>
                      Executive Summary
                      <span className={`ml-4 ${
                        analysisResults.overall_risk_rating === 'High Risk' ? 'text-red-500' :
                        analysisResults.overall_risk_rating === 'Medium Risk' ? 'text-yellow-500' :
                        'text-green-500'
                      }`}>
                        ({analysisResults.overall_risk_rating})
                      </span>
                    </CardTitle>
                    <CardDescription>Comprehensive analysis of {analysisResults.documents_analyzed?.length || 0} document(s)</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div 
                  className="analysis-content"
                  dangerouslySetInnerHTML={{ __html: analysisResults.summary }}
                />
              </CardContent>
            </Card>

            {/* Key Findings */}
            {analysisResults.key_findings && analysisResults.key_findings.length > 0 && (
              <Card className="result-card">
                <CardHeader>
                  <CardTitle>Key Findings</CardTitle>
                  <CardDescription>Critical insights discovered across all documents</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="differences-list">
                    {analysisResults.key_findings.map((finding, index) => (
                      <li key={index} className="difference-item">
                        {finding}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Red Flags */}
            {analysisResults.red_flags && analysisResults.red_flags.length > 0 && (
              <Card className="result-card">
                <CardHeader>
                  <CardTitle>
                    <XCircle className="inline mr-2 text-red-500" size={24} />
                    Red Flags
                  </CardTitle>
                  <CardDescription>Critical issues requiring immediate attention</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="differences-list">
                    {analysisResults.red_flags.map((flag, index) => (
                      <li key={index} className="difference-item text-red-500">
                        {flag}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Issues by Category */}
            {analysisResults.issues && analysisResults.issues.length > 0 && (
              <Card className="result-card">
                <CardHeader>
                  <CardTitle>Detailed Issues Analysis</CardTitle>
                  <CardDescription>Issues categorized by type and severity</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="clauses-grid">
                    {analysisResults.issues.map((issue, index) => (
                      <div key={index} className="clause-item" style={{ overflowWrap: 'break-word', wordWrap: 'break-word' }}>
                        <div className="clause-header" style={{ flexWrap: 'wrap' }}>
                          <AlertCircle size={20} className={
                            issue.severity === 'High' ? 'text-red-500' :
                            issue.severity === 'Medium' ? 'text-yellow-500' :
                            'text-green-500'
                          } />
                          <h4 style={{ overflowWrap: 'break-word', wordBreak: 'break-word', flex: '1 1 auto' }}>{issue.category}: {issue.issue}</h4>
                          <span className={`impact-badge ${
                            issue.severity === 'High' ? 'text-red-500' :
                            issue.severity === 'Medium' ? 'text-yellow-500' :
                            'text-green-500'
                          }`}>
                            {issue.severity}
                          </span>
                        </div>
                        <div 
                          className="analysis-content"
                          style={{ overflowWrap: 'break-word', wordWrap: 'break-word', whiteSpace: 'pre-wrap' }}
                          dangerouslySetInnerHTML={{ __html: issue.description }}
                        />
                        <p className="clause-location" style={{ overflowWrap: 'break-word', wordWrap: 'break-word' }}>
                          <strong>Source:</strong> {issue.source_document}
                        </p>
                        <div className="clause-analysis">
                          <strong>Recommendation:</strong> {issue.recommendation}
                        </div>
                      </div>
                    ))}
        <ReportIssueBar toolName="Due Diligence" />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recommendations */}
            <Card className="result-card">
              <CardHeader>
                <CardTitle>Recommendations & Next Steps</CardTitle>
                <CardDescription>Strategic recommendations based on analysis</CardDescription>
              </CardHeader>
              <CardContent>
                <div 
                  className="analysis-content"
                  dangerouslySetInnerHTML={{ __html: analysisResults.recommendations }}
                />
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default DueDiligence;

